import { Volume2 } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";

function speak(text) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.78;
  u.pitch = 1;
  const voices = window.speechSynthesis.getVoices();
  const gb = voices.find((v) => /en[-_]GB/i.test(v.lang)) || voices.find((v) => /^en/i.test(v.lang));
  if (gb) u.voice = gb;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
}

function Pronounce({ phonetic, say, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={() => speak(say)}
      className="group/pron mt-3 inline-flex items-center gap-2 rounded-md border border-clan-accent/40 px-3 py-1.5 transition-colors duration-300 hover:bg-clan-accent/10 focus:outline-none focus:ring-2 focus:ring-clan-accent"
      aria-label="Play pronunciation"
    >
      <Volume2 className="h-3.5 w-3.5 text-clan-accent" />
      <span className="font-mono text-[11px] tracking-wide text-clan-muted">{phonetic}</span>
    </button>
  );
}

export function NameMeaning() {
  const { t } = useLanguage();
  const nm = t.name;

  return (
    <section id="name" data-testid="name-section" className="relative bg-clan-surface py-28 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <Overline>{nm.overline}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
              {nm.title}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-clan-muted">{nm.intro}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {nm.terms.map((term, i) => (
            <Reveal key={term.term} delay={0.1 + i * 0.08} y={30}>
              <div
                data-testid={`name-term-${i}`}
                className="group h-full rounded-lg border border-clan-ink/10 bg-clan-bg/40 p-7 transition-colors duration-300 hover:border-clan-accent/40"
              >
                <p className="font-serif text-3xl text-clan-accent">{term.term}</p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-clan-muted">{term.gloss}</p>
                <p className="mt-5 text-sm leading-relaxed text-clan-muted">{term.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* The two forms compared */}
        <Reveal delay={0.2}>
          <div className="mt-8 grid gap-5 rounded-lg border border-clan-accent/25 bg-clan-accent/5 p-8 md:grid-cols-2 lg:p-10">
            <div className="border-l-2 border-clan-accent pl-6">
              <p className="font-serif text-2xl italic text-clan-ink lg:text-3xl">{nm.compareA}</p>
              <p className="mt-2 text-sm text-clan-muted">{nm.compareAMeaning}</p>
              <Pronounce testid="pron-clann-domhnaill" phonetic="klown · GHOH-il" say="klown ghoh il" />
            </div>
            <div className="border-l-2 border-clan-accent pl-6">
              <p className="font-serif text-2xl italic text-clan-ink lg:text-3xl">{nm.compareB}</p>
              <p className="mt-2 text-sm text-clan-muted">{nm.compareBMeaning}</p>
              <Pronounce testid="pron-clann-mhic-domhnaill" phonetic="klown · vic · GHOH-il" say="klown vic ghoh il" />
            </div>
            <p className="md:col-span-2 border-t border-clan-ink/10 pt-6 text-sm leading-relaxed text-clan-muted">{nm.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
